import debounce from 'lodash/debounce';
import flat from 'flat';
import isEmpty from 'lodash/isEmpty';
import { FormikErrors, FormikState, useFormik } from 'formik';
import { useDeepCompareEffect } from 'react-use';
import { useMemo } from 'react';

import environment from '../../utils/environment';
import tracking from '../../utils/tracking';
import { FormikWrapperProps } from './FormikWrapper';
import { TrackingEvent } from '../../utils/tracking/types';

export function useFormikErrorTracker<Values>(errors: FormikErrors<Values>) {
  useDeepCompareEffect(() => {
    const flatErrors = flat(errors);
    if (!isEmpty(flatErrors)) tracking.event(TrackingEvent.FORM_VALIDATION_ERROR, flatErrors);
  }, [errors]);
  return null;
}

const sessionProps = <Values,>(key?: string) => {
  const formikSessionItem = environment.isClient && key ? window.sessionStorage.getItem(key) : undefined;
  return formikSessionItem ? (JSON.parse(formikSessionItem) as FormikState<Values>) : undefined;
};

const saveForm = debounce((key: string, data: any) => {
  if (environment.isClient) window.sessionStorage.setItem(key, JSON.stringify(data));
}, 300);

export function useFormikSessionStorage<Values>({ formikSessionStateKey, ...formikProps }: FormikWrapperProps<Values>) {
  const savedFormikState = useMemo(() => sessionProps<Values>(formikSessionStateKey), [formikSessionStateKey]);

  const injectedValuesFormikProps = !savedFormikState
    ? formikProps
    : {
        ...formikProps,
        initialValues: savedFormikState.values,
        initialErrors: savedFormikState.errors,
        initialStatus: savedFormikState.status,
        initialTouched: savedFormikState.touched,
      };

  const bag = useFormik<Values>(injectedValuesFormikProps);

  // Could useDeepCompareEffect potentially be having a performance hit if session storage is not being utilised?
  useDeepCompareEffect(() => {
    if (formikSessionStateKey) saveForm(formikSessionStateKey, bag);
  }, [formikSessionStateKey, bag]);

  return bag;
}
